import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the corporate-documents-for-abroad guide. */
const TITLE = "Giấy tờ công ty Thái Lan dùng ở nước ngoài — chứng nhận sao cho được chấp nhận";
const DESC =
  "Giấy tờ pháp nhân Thái Lan cần lớp chứng nhận nào, thứ tự thực hiện đúng, và những điểm cần kiểm tra trước khi nộp để tránh bị trả lại.";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Giấy tờ công ty nào thường phải chứng nhận?",
    answer:
      "Thường gồm giấy chứng nhận đăng ký doanh nghiệp, danh sách cổ đông, điều lệ công ty, biên bản họp hội đồng, giấy uỷ quyền và giấy chứng nhận đăng ký thuế giá trị gia tăng.",
  },
  {
    question: "Có cần bản cấp mới từ cơ quan đăng ký không?",
    answer:
      "Nên có. Nhiều cơ quan tiếp nhận yêu cầu bản do Cục Phát triển Doanh nghiệp cấp trong vòng 1–3 tháng, có chữ ký và con dấu của người có thẩm quyền.",
  },
  {
    question: "Thứ tự đúng là gì?",
    answer:
      "Xin bản cấp mới → công chứng Notary cho giấy tờ do công ty tự lập → chứng nhận tại Cục Lãnh sự → dịch → hợp pháp hoá tại đại sứ quán nếu cần.",
  },
  {
    question: "Biên bản họp có cần công chứng không?",
    answer:
      "Biên bản và nghị quyết là giấy tờ do công ty tự lập, nên thường cần luật sư công chứng xác nhận chữ ký của người ký trước khi chứng nhận tiếp.",
  },
  {
    question: "Dịch sang tiếng gì?",
    answer:
      "Theo yêu cầu của cơ quan tiếp nhận, thường là tiếng Anh hoặc ngôn ngữ chính thức của nước đó. Một số nước yêu cầu dịch viên được công nhận.",
  },
  {
    question: "Có cần nộp bản gốc hay bản sao là đủ?",
    answer:
      "Nhiều nơi yêu cầu bản gốc đã chứng nhận đầy đủ. Hãy chuẩn bị thêm bộ bản sao để lưu và để dùng cho các bước song song.",
  },
  {
    question: "Điều gì làm chậm hồ sơ công ty nhất?",
    answer:
      "Bản đăng ký quá hạn, thiếu công chứng cho giấy tờ nội bộ, tên công ty viết khác nhau giữa các bản dịch, và thiếu bản dịch của con dấu.",
  },
];

export const Route = createFileRoute("/vi/blog/company-documents-for-abroad")({
  component: ViCorporateGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "giấy tờ công ty Thái Lan, chứng nhận hồ sơ doanh nghiệp Thái Lan, dịch giấy tờ công ty, hợp pháp hoá giấy tờ doanh nghiệp",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Giấy chứng nhận đăng ký doanh nghiệp", note: "Xin bản cấp mới từ Cục Phát triển Doanh nghiệp" },
  { d: "Danh sách cổ đông (บอจ.5)", note: "Nhiều cơ quan yêu cầu bản cấp trong vòng 1–3 tháng" },
  { d: "Điều lệ và bản ghi nhớ thành lập", note: "Dùng khi cần chứng minh quyền hạn và phạm vi kinh doanh" },
  { d: "Biên bản họp và nghị quyết", note: "Giấy tờ tự lập — cần công chứng Notary xác nhận chữ ký" },
  { d: "Giấy uỷ quyền cho người đại diện", note: "Nêu rõ phạm vi ký, nhận và nộp hồ sơ" },
  { d: "Giấy chứng nhận đăng ký VAT và báo cáo tài chính", note: "Nếu cơ quan tiếp nhận yêu cầu chứng minh hoạt động" },
];

const ORDER: string[] = [
  "Lấy danh mục chính thức của cơ quan hoặc đối tác ở nước ngoài",
  "Xin bản cấp mới của giấy tờ đăng ký doanh nghiệp",
  "Công chứng Notary cho biên bản, nghị quyết và giấy uỷ quyền",
  "Chứng nhận tại Cục Lãnh sự Bộ Ngoại giao",
  "Dịch toàn văn kèm con dấu, tem và chú thích",
  "Hợp pháp hoá tại đại sứ quán nước tiếp nhận nếu cần, rồi rà soát tên và ngày tháng",
];

const CHECKS: string[] = [
  "Tên công ty viết thống nhất trong mọi bản dịch",
  "Người ký đúng là người có thẩm quyền theo giấy đăng ký",
  "Bản đăng ký còn trong thời hạn mà cơ quan tiếp nhận chấp nhận",
  "Bản dịch bao gồm cả con dấu và dòng chú thích",
  "Đủ số bộ theo yêu cầu, kèm một bộ lưu nội bộ",
  "Thứ tự chứng nhận đúng, không dịch trước khi chứng nhận",
];

function ViCorporateGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="vi" />
      <p className="answer-summary speakable-summary">
        Hồ sơ doanh nghiệp Thái Lan dùng ở nước ngoài cần hai loại xử lý khác nhau: giấy tờ do cơ quan đăng ký cấp thì xin
        bản mới rồi chứng nhận, còn biên bản và giấy uỷ quyền do công ty tự lập thì phải công chứng Notary trước. Sau đó
        mới đến Cục Lãnh sự, dịch, và hợp pháp hoá tại đại sứ quán nếu được yêu cầu.
      </p>

      <h2>1. Giấy tờ thường dùng</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Thứ tự thực hiện</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Điểm kiểm tra trước khi nộp</h2>
      <ul>
        {CHECKS.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          Cục Phát triển Doanh nghiệp (Bộ Thương mại Thái Lan)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/corporate" className="text-primary underline">
            Dịch vụ giấy tờ doanh nghiệp
          </Link>
        </li>
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
        <li>
          <Link to="/vi/notary" className="text-primary underline">
            Công chứng Notary bởi luật sư
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Yêu cầu của từng cơ quan và từng nước có thể khác nhau, nên hãy xác nhận danh
        mục chính thức trước khi bắt đầu. Nếu bạn không muốn tự theo từng bước, đội ngũ hơn 15 năm kinh nghiệm của chúng
        tôi có thể tư vấn và thực hiện toàn bộ — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
